import React, { useRef, useState } from "react";
import { api } from "../lib/api";
import { useAuth } from "../lib/auth";
import { toast } from "sonner";
import Avatar from "./Avatar";
import { Camera, Upload, Trash2 } from "lucide-react";
import { compressImage, isMobileDevice } from "../lib/image";

const MAX = 3 * 1024 * 1024;

export default function ProfilePhotoEditor() {
  const { user, refresh } = useAuth();
  const fileRef = useRef(null);
  const camRef = useRef(null);
  const [busy, setBusy] = useState(false);
  const [preview, setPreview] = useState(null);
  const isMobile = isMobileDevice();

  const onPick = async (e) => {
    let f = e.target.files?.[0];
    e.target.value = "";
    if (!f) return;
    if (!/^image\/(jpe?g|png|webp|heic|heif)$/i.test(f.type)) return toast.error("Use JPG, PNG, WEBP or HEIC.");
    // Local preview (original)
    const reader = new FileReader();
    reader.onload = () => setPreview(reader.result);
    reader.readAsDataURL(f);
    setBusy(true);
    try {
      // Auto-compress so phone-camera 5-10MB files stay under our 3MB upload limit
      const compressed = await compressImage(f, { maxDim: 1024, quality: 0.88 });
      if (compressed.size > MAX) {
        setPreview(null);
        return toast.error("Image too large even after compression — try a smaller photo.");
      }
      const fd = new FormData();
      fd.append("file", compressed);
      await api.post("/upload/profile-photo", fd, { headers: { "Content-Type": "multipart/form-data" } });
      await refresh();
      setPreview(null);
      toast.success("Profile photo updated");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Upload failed");
      setPreview(null);
    } finally { setBusy(false); }
  };

  const onRemove = async () => {
    if (!window.confirm("Remove your profile photo?")) return;
    setBusy(true);
    try {
      await api.delete("/upload/profile-photo");
      await refresh();
      toast.success("Photo removed");
    } catch (err) { toast.error("Failed to remove"); }
    finally { setBusy(false); }
  };

  const hasPhoto = !!user.profile_image;

  return (
    <div className="nv-card p-5" data-testid="profile-photo-editor">
      <h3 className="text-h3">Profile Photo</h3>
      <p className="text-xs text-slate-500 mt-0.5">Upload a JPG, PNG, WEBP or HEIC image (max 3MB).</p>
      <div className="mt-5 flex items-center gap-5">
        <div className="relative">
          {preview ? (
            <img src={preview} alt="preview" className="h-24 w-24 rounded-full object-cover ring-2 ring-white shadow" />
          ) : (
            <Avatar name={user.full_name} gender={user.gender} src={user.profile_image} size="xl" className="!h-24 !w-24 !text-2xl" />
          )}
          {busy && <div className="absolute inset-0 rounded-full bg-white/70 backdrop-blur-sm flex items-center justify-center text-xs font-semibold text-slate-600">Uploading…</div>}
        </div>
        <div className="flex-1 flex flex-wrap gap-2">
          <input ref={fileRef} type="file" accept="image/jpeg,image/jpg,image/png,image/webp,image/heic,image/heif" className="hidden" onChange={onPick} data-testid="photo-file-input" />
          <input ref={camRef} type="file" accept="image/*" capture="user" className="hidden" onChange={onPick} data-testid="photo-camera-input" />
          <button type="button" disabled={busy} onClick={() => fileRef.current?.click()} className="inline-flex items-center gap-2 text-sm font-semibold px-3.5 py-2 rounded-md bg-sky-800 hover:bg-sky-900 text-white disabled:opacity-60" data-testid="photo-upload-btn">
            <Upload size={14} /> Upload Photo
          </button>
          <button type="button" disabled={busy} onClick={() => camRef.current?.click()} className="inline-flex items-center gap-2 text-sm font-semibold px-3.5 py-2 rounded-md bg-white border border-slate-200 hover:bg-slate-50 disabled:opacity-60" data-testid="photo-camera-btn">
            <Camera size={14} /> {isMobile ? "Take Selfie" : "Use Camera"}
          </button>
          {hasPhoto && (
            <button type="button" disabled={busy} onClick={onRemove} className="inline-flex items-center gap-2 text-sm font-semibold px-3.5 py-2 rounded-md bg-white border border-slate-200 text-rose-600 hover:bg-rose-50 disabled:opacity-60" data-testid="photo-remove-btn">
              <Trash2 size={14} /> Remove
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
